// KYC Types and Models

// KYC Problem types from API
export type KYCProblem =
  | "business_name_mismatch"
  | "business_logo_mismatch"
  | "business_email_mismatch"
  | "business_phone_number_mismatch"
  | "address_mismatch"
  | "gst_number_mismatch"
  | "aadhaar_document_mismatch"
  | "pan_document_mismatch"
  | "address_proof_document_mismatch"
  | "video_kyc_mismatch"
  | "kyc_already_exists"
  | "other";

export interface KYCState {
  isLoading: boolean;
  error: string | null;
  currentStep: number;
  kycStatus:
    | "PENDING"
    | "IN_PROGRESS"
    | "APPROVED"
    | "REJECTED"
    | "PENDING_VERIFICATION";
  rejectionReason?: string;
  kycProblems?: KYCProblem[]; // Array of specific problems from API
  rejectedSteps?: {
    businessDetails: boolean;
    personalDetails: boolean;
    documentUpload: boolean;
    addressVerification: boolean;
    videoVerification: boolean;
  }; // Track which steps have rejection issues
  completedSteps: {
    businessDetails: boolean;
    personalDetails: boolean;
    documentUpload: boolean;
    addressVerification: boolean;
    videoVerification: boolean;
  };
  kycData?: KYCData;
  isResubmissionMode: boolean; // Track if user is resubmitting after rejection
  // Track the last meaningful action for polling-based components to observe
  lastAction?: string;
}

export interface KYCData {
  id?: string;
  business_name?: string;
  business_logo?: string;
  business_email?: string;
  business_phone_number?: string;
  address?: {
    address_line_one?: string;
    address_line_two?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    id?: string;
    store_type?: string;
    created_at?: string;
    updated_at?: string;
    deleted_at?: string | null;
  };
  time_zone?: string;
  currency?: string;
  gst_number?: string;
  aadhaar_document_front?: string;
  aadhaar_document_back?: string;
  pan_document?: string;
  address_proof_document?: string;
  video_file?: string;
  status?: string;
  stage?: string; // Added stage field from API response
  rejection_reason?: string;
  kyc_problem?: KYCProblem[] | null; // Array of problems from API
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
  user_id?: string;
}

// Form Data Types
export interface BusinessDetailsFormData {
  business_name: string;
  business_logo: File | null;
  business_email: string;
  business_phone_number: string;
  business_address: string;
  time_zone: string;
  currency: string;
  gst_number: string;
}

export interface DocumentUploadFormData {
  aadhaar_document_front: File | null;
  aadhaar_document_back: File | null;
  pan_document: File | null;
}

export interface AddressVerificationFormData {
  address_proof_document: File | null;
}

export interface VideoVerificationFormData {
  video_kyc_file: File | null;
}

// API Response Types
export interface KYCStatusResponse {
  valid: boolean;
  message: string;
  data: KYCData;
}

export interface KYCSubmissionResponse extends Record<string, unknown> {
  valid: boolean; // All KYC endpoints use 'valid' according to swagger.json
  message: string;
  data?: Record<string, unknown>; // The data structure varies by endpoint
}

// Redux Action Types
export interface FetchKYCStatusAction {
  type: "kyc/fetchKYCStatus";
  payload: {
    storeId: string;
  };
}

export interface SubmitBusinessDetailsAction {
  type: "kyc/submitBusinessDetails";
  payload: {
    storeId: string;
    formData: FormData;
  };
}

export interface SubmitPersonalDetailsAction {
  type: "kyc/submitPersonalDetails";
  payload: {
    storeId: string;
    formData: FormData;
  };
}

export interface UploadDocumentsAction {
  type: "kyc/uploadDocuments";
  payload: {
    storeId: string;
    formData: FormData;
  };
}

export interface UploadAddressProofAction {
  type: "kyc/uploadAddressProof";
  payload: {
    storeId: string;
    formData: FormData;
  };
}

export interface UploadVideoKYCAction {
  type: "kyc/uploadVideoKYC";
  payload: {
    storeId: string;
    formData: FormData;
  };
}

// Validation Schemas (for Zod)
export interface BusinessDetailsSchema {
  business_name: string;
  business_email: string;
  business_phone_number: string;
  business_address: string;
  time_zone: string;
  currency: string;
  gst_number: string;
}

// File validation types
export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

export interface FileUploadConfig {
  maxSize: number; // in bytes
  allowedTypes: string[];
  required: boolean;
}

// Step configuration
export interface KYCStep {
  id: number;
  title: string;
  description: string;
  component: string;
  isCompleted: boolean;
  isActive: boolean;
}

// Currency and timezone options
export interface CurrencyOption {
  code: string;
  name: string;
  symbol: string;
}

export interface TimezoneOption {
  value: string;
  label: string;
  offset: string;
}

// Common constants
export const KYC_STEPS = {
  PERSONAL_BUSINESS_DETAILS: 1,
  DOCUMENT_UPLOAD: 2,
  ADDRESS_VERIFICATION: 3,
  VIDEO_VERIFICATION: 4,
  STATUS_DISPLAY: 5,
} as const;

export const KYC_STATUS = {
  PENDING: "PENDING",
  IN_PROGRESS: "IN_PROGRESS",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  PENDING_VERIFICATION: "PENDING_VERIFICATION",
} as const;

export const FILE_UPLOAD_CONFIG = {
  BUSINESS_LOGO: {
    maxSize: 2 * 1024 * 1024, // 2MB
    allowedTypes: ["image/jpeg", "image/png"],
    required: false,
  },
  DOCUMENTS: {
    maxSize: 2 * 1024 * 1024, // 2MB
    allowedTypes: ["image/jpeg", "image/png"],
    required: true,
  },
  VIDEO: {
    maxSize: 50 * 1024 * 1024, // 50MB
    allowedTypes: ["video/mp4", "video/webm", "video/quicktime"],
    required: true,
  },
} as const;

// Utility functions for KYC problem mapping
export const mapProblemsToSteps = (problems: KYCProblem[]) => {
  const rejectedSteps = {
    businessDetails: false,
    personalDetails: false,
    documentUpload: false,
    addressVerification: false,
    videoVerification: false,
  };

  problems.forEach((problem) => {
    switch (problem) {
      case "business_name_mismatch":
      case "business_logo_mismatch":
      case "business_email_mismatch":
      case "business_phone_number_mismatch":
        rejectedSteps.businessDetails = true;
        break;
      case "gst_number_mismatch":
        rejectedSteps.personalDetails = true;
        break;
      case "aadhaar_document_mismatch":
      case "pan_document_mismatch":
        rejectedSteps.documentUpload = true;
        break;
      case "address_mismatch":
      case "address_proof_document_mismatch":
        rejectedSteps.addressVerification = true;
        break;
      case "video_kyc_mismatch":
        rejectedSteps.videoVerification = true;
        break;
      default:
        // For "kyc_already_exists" and "other", we don't highlight specific steps
        break;
    }
  });

  return rejectedSteps;
};

// Map KYC problems to specific field names that can be updated via PATCH
export const getUpdatableFieldsFromProblems = (
  problems: KYCProblem[],
): string[] => {
  const updatableFields = new Set<string>();

  problems.forEach((problem) => {
    switch (problem) {
      case "business_name_mismatch":
        updatableFields.add("business_name");
        break;
      case "business_logo_mismatch":
        updatableFields.add("business_logo");
        break;
      case "business_email_mismatch":
        updatableFields.add("business_email");
        break;
      case "business_phone_number_mismatch":
        updatableFields.add("business_phone_number");
        break;
      case "address_mismatch":
        // Address mismatch could affect multiple address fields
        updatableFields.add("address_line_one");
        updatableFields.add("address_line_two");
        updatableFields.add("city");
        updatableFields.add("state");
        updatableFields.add("postal_code");
        break;
      case "gst_number_mismatch":
        updatableFields.add("gst_number");
        break;
      case "aadhaar_document_mismatch":
        updatableFields.add("aadhaar_document_front");
        updatableFields.add("aadhaar_document_back");
        break;
      case "pan_document_mismatch":
        updatableFields.add("pan_document");
        break;
      case "address_proof_document_mismatch":
        updatableFields.add("address_proof_document");
        break;
      case "video_kyc_mismatch":
        updatableFields.add("video_kyc_file");
        break;
      // For "kyc_already_exists" and "other", no specific fields can be updated
      default:
        break;
    }
  });

  return Array.from(updatableFields);
};

// Get human-readable problem descriptions
export const getProblemDescription = (problem: KYCProblem): string => {
  switch (problem) {
    case "business_name_mismatch":
      return "Business name doesn't match the provided documents";
    case "business_logo_mismatch":
      return "Business logo is unclear or inappropriate";
    case "business_email_mismatch":
      return "Business email address is invalid or unverifiable";
    case "business_phone_number_mismatch":
      return "Business phone number is invalid or unverifiable";
    case "address_mismatch":
      return "Business address doesn't match the provided documents";
    case "gst_number_mismatch":
      return "GST number is invalid or doesn't match business details";
    case "aadhaar_document_mismatch":
      return "Aadhaar document is unclear, invalid, or doesn't match personal details";
    case "pan_document_mismatch":
      return "PAN document is unclear, invalid, or doesn't match personal details";
    case "address_proof_document_mismatch":
      return "Address proof document is unclear or doesn't match the provided address";
    case "video_kyc_mismatch":
      return "Video KYC verification failed or video quality is insufficient";
    case "kyc_already_exists":
      return "KYC already exists for this business or individual";
    case "other":
      return "Other verification issues found";
    default:
      return "Unknown verification issue";
  }
};
